import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ReactComponent as Undo } from "../../../../../icons/undo.svg";
import {
  getBessGeneratorCharges,
  postBessGeneratorChargesChange
} from "../../../../../Rest_api/RestApi";

type GeneratorRow = {
  generator_id: number;
  plant_name: string;
  source: string;
  plant_capacity_mw: number;
  commission_date: string;
  decommission_date: string;
  fixed_charge: number;
  [key: string]: any; // For dynamic vc_YYYY columns
};

interface ModTableProps {
  maxHeight?: string;
  mode?: "normal" | "edit";
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: (data: any) => void;
  loading?: boolean;
  handleUpdateReview?: (data: any) => void;
  refreshKey: any;
  setRefreshKey: any;
}

interface EditingCell {
  generatorId: number;
  field: string; // e.g., "vc_2025"
}

export function DeficitBidTable({
  maxHeight = "calc(-300px + 100vh)",
  mode = "edit",
  setModalOpen,
  handleSubmit,
  refreshKey,
  setRefreshKey,
  loading = false,
  handleUpdateReview,
}: ModTableProps) {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [draftValue, setDraftValue] = useState<string>("");
  const [data, setData] = useState<GeneratorRow[]>([]);
  const [yearColumns, setYearColumns] = useState<string[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [apiLoading, setApiLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setApiLoading(true);
        const response = await getBessGeneratorCharges();

        setSessionId(response.session_id);
        setYearColumns(response.year_columns);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching generator charges:", error);
      } finally {
        setApiLoading(false);
      }
    };

    fetchData();
  }, []);

  const valueKey = useCallback(
    (generatorId: number, field: string) => `${generatorId}__${field}`,
    []
  );

  const parseValueKey = useCallback((k: string) => {
    const [generatorId, field] = k.split("__");
    return { generatorId: Number(generatorId), field };
  }, []);

  const findOriginalRow = useCallback(
    (generatorId: number) => data.find((r) => r.generator_id === generatorId),
    [data]
  );

  const getOriginalValue = useCallback(
    (generatorId: number, field: string) => {
      const r = findOriginalRow(generatorId);
      const v = r ? r[field] : undefined;
      return v === undefined || v === null ? "-" : v;
    },
    [findOriginalRow]
  );

  const startEdit = (generatorId: number, field: string, original: any) => {
    if (mode !== "edit") return;
    if (
      editingCell &&
      editingCell.generatorId === generatorId &&
      editingCell.field === field
    )
      return;
    const k = valueKey(generatorId, field);
    const currentValue =
      localValues[k] !== undefined ? localValues[k] : original;
    setDraftValue(
      currentValue === "-" || currentValue == null ? "" : String(currentValue)
    );
    setEditingCell({ generatorId, field });
  };

  const confirmEdit = () => {
    if (!editingCell) return;
    const { generatorId, field } = editingCell;
    const k = valueKey(generatorId, field);
    const trimmed = draftValue.trim();
    const valueToSave =
      trimmed === "" ? "" : isNaN(Number(trimmed)) ? trimmed : Number(trimmed);

    const original = getOriginalValue(generatorId, field);
    const prev = Object.prototype.hasOwnProperty.call(localValues, k)
      ? localValues[k]
      : original;

    if (valueToSave === prev) {
      setEditingCell(null);
      setDraftValue("");
      return;
    }

    setLocalValues((p) => ({ ...p, [k]: valueToSave }));
    setEditingCell(null);
    setDraftValue("");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setDraftValue("");
  };

  const revertCell = (generatorId: number, field: string) => {
    const k = valueKey(generatorId, field);
    setLocalValues((p) => {
      if (!(k in p)) return p;
      const { [k]: _, ...rest } = p;
      return rest;
    });
  };

  const revertAll = () => {
    if (!Object.keys(localValues).length) return;
    setLocalValues({});
    cancelEdit();
  };

  const handleUpdateClick = async () => {
    try {
      setSubmitLoading(true);

      // Group changes by generator_id
      const generatorChanges: Record<number, any> = {};

      for (const k of Object.keys(localValues)) {
        const { generatorId, field } = parseValueKey(k);

        if (!generatorChanges[generatorId]) {
          generatorChanges[generatorId] = {
            generator_id: generatorId,
          };
        }

        // Only include VC fields (vc_2025, vc_2026, etc.)
        if (field.startsWith('vc_')) {
          generatorChanges[generatorId][field] = localValues[k];
        }
      }

      // Convert to array format as required by API
      const payload = Object.values(generatorChanges);

      // Call the provided callbacks if they exist
      handleSubmit?.(payload);
      handleUpdateReview?.(payload);

      // Clear local changes after successful submission
      setLocalValues({});

      // Optionally refresh the data
      const refreshedData = await getBessGeneratorCharges();
      setData(refreshedData.data);

      // Close modal or show success message
      setModalOpen(false);

    } catch (error) {
      console.error("Error updating generator charges:", error);
      // You might want to show an error toast/notification here
    } finally {
      setSubmitLoading(false);
    }
  };

  if (apiLoading) {
    return (
      <div className="w-full h-full text-center flex justify-center items-center text-sm mt-10 text-gray-500">
        Loading...
      </div>
    );
  }

  if (!data || !data.length) {
    return (
      <div className="w-full h-full text-center flex justify-center items-center text-sm mt-10 text-gray-500">
        No data available
      </div>
    );
  }

  const localSize = Object.keys(localValues).length;
  const isEditMode = mode === "edit";

  return (
    <div
      className="relative overflow-auto capitalize text-nowrap rounded-lg border border-[#F1935C]"
      style={{ maxHeight }}
    >
      <table id="generator-charges-table" className="table-auto bid_table mb-0">
        <thead className="sticky top-0 z-50 text-[#F1935C] bg-[#FFF8F4]">
          <tr>
            <th className="sticky left-0 z-30 px-4 py-3 text-xs text-center rounded-tl-xl min-w-[150px] bg-[#FFF8F4]">
              Generator Name
            </th>
            <th className="px-4 py-3 text-xs text-center min-w-[90px]">Source</th>
            <th className="px-4 py-3 text-xs text-center min-w-[110px]">
              Commission Date
            </th>
            <th className="px-4 py-3 text-xs text-center min-w-[110px]">
              Decommission Date
            </th>
            <th className="px-4 py-3 text-xs text-center min-w-[90px]">
              Plant Capacity
            </th>
            {yearColumns.map((vcCol) => {
              const year = vcCol.replace("vc_", "");
              return (
                <th
                  key={vcCol}
                  className="px-4 py-3 text-xs text-center min-w-[80px] text-[#34656D]"
                >
                  VC {year}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="text-[#8C8A96] font-normal text-xs">
          {data.map((row) => {
            return (
              <tr
                key={row.generator_id}
                className="border-b border-gray-200 hover:bg-gray-100 text-center"
              >
                <td className="sticky left-0 z-10 bg-white py-3 px-4 text-left">
                  {row.plant_name || "-"}
                </td>
                <td className="bg-white py-3 px-4">{row.source || "-"}</td>
                <td className="bg-white py-3 px-4">
                  {row.commission_date
                    ? new Date(row.commission_date).toLocaleDateString()
                    : "-"}
                </td>
                <td className="bg-white py-3 px-4">
                  {row.decommission_date
                    ? new Date(row.decommission_date).toLocaleDateString()
                    : "-"}
                </td>
                <td className="bg-white py-3 px-4">
                  {row.plant_capacity_mw !== null && row.plant_capacity_mw !== undefined
                    ? row.plant_capacity_mw
                    : "-"}
                </td>

                {yearColumns.map((vcField) => {
                  const k = valueKey(row.generator_id, vcField);
                  const isEditing =
                    editingCell &&
                    editingCell.generatorId === row.generator_id &&
                    editingCell.field === vcField;
                  const originalValue = row[vcField];
                  const display =
                    localValues[k] !== undefined
                      ? localValues[k]
                      : originalValue === null || originalValue === undefined
                        ? "-"
                        : originalValue;
                  const differs =
                    Object.prototype.hasOwnProperty.call(localValues, k) &&
                    localValues[k] !== originalValue;

                  return (
                    <td
                      key={vcField}
                      className={`bg-white relative py-3 px-4 cursor-${isEditMode ? "pointer" : "default"
                        }`}
                      onClick={() => {
                        // if (isEditMode && !isEditing)
                        //   startEdit(row.generator_id, vcField, originalValue);
                      }}
                      data-edited={differs ? "true" : "false"}
                    >
                      {/* {differs && !isEditing && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            revertCell(row.generator_id, vcField);
                          }}
                          className="absolute top-2 z-[9] right-2 bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] px-2 py-[2px] leading-tight shadow"
                          title="Revert this cell"
                        >
                          UNDO
                        </button>
                      )} */}
                      {!isEditing && (
                        <span
                          className={
                            differs ? "text-primary font-semibold" : ""
                          }
                        >
                          {display}
                        </span>
                      )}
                      {/* {isEditing && (
                        <div className="absolute z-[9999] inset-0 flex items-center justify-center">
                          <div className="flex border border-gray-300 shadow-lg bg-white">
                            <input
                              autoFocus
                              step="any"
                              inputMode="decimal"
                              type="number"
                              className="w-24 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0"
                              value={draftValue}
                              onChange={(e) => setDraftValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") confirmEdit();
                                else if (e.key === "Escape") cancelEdit();
                              }}
                              onBlur={(e) => {
                                if (
                                  editingCell &&
                                  !e.currentTarget.contains(
                                    e.relatedTarget as Node
                                  )
                                ) {
                                  cancelEdit();
                                }
                              }}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmEdit();
                              }}
                              className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0"
                              title="Save"
                            >
                              ✓
                            </button>
                          </div>
                        </div>
                      )} */}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {isEditMode && (
        <div className="sticky bottom-0 left-0 right-0 z-[80] bg-white border-t px-2 py-2 flex gap-x-4 justify-between items-center">
          <div>
            <button
              onClick={revertAll}
              disabled={localSize === 0}
              className="flex items-center gap-1 px-4 py-2 text-sm text-[#34656D] font-medium disabled:opacity-50"
            >
              <Undo className="w-4 h-4" />
              Revert All
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setModalOpen(false)}
              className="flex items-center gap-1 px-6 py-2 text-xs rounded-xl border-2 border-[#8C8A96] text-[#8C8A96]"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateClick}
              className="flex items-center gap-1 px-6 py-2 text-xs rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
              disabled={localSize === 0 || submitLoading}
            >
              {submitLoading ? "Saving..." : "Update & Review"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}